/**
 * Scroll Animations System
 * Handles scroll-triggered animations with IntersectionObserver for optimal performance
 */

// Constants
const SCROLL_ANIMATION_TRIGGER_CLASSNAME = "scroll-trigger";
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = "scroll-trigger--offscreen";
const SCROLL_ANIMATION_DESIGN_MODE_CLASSNAME = "scroll-trigger--design-mode";
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = "animate--zoom-in";
const ANIMATE_PREFIX = "animate--";

// Animation types supported (classes like animate--fade-in, animate--slide-in, etc.)
const SUPPORTED_ANIMATION_TYPES = ["fade-in", "slide-in", "zoom-in", "fade-up", "slide-up"];

// Global state for zoom animations (for cleanup)
const zoomAnimationState = new WeakMap();

/**
 * Utility: Throttle function for performance optimization
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
function throttle(func, limit = 100) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Utility: Check if reduced motion is preferred
 * @returns {boolean} True if reduced motion is preferred
 */
function prefersReducedMotion() {
  const respectReducedMotion = document.body.dataset.respectReducedMotion === 'true';
  if (!respectReducedMotion) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Get all animation trigger elements (scroll-trigger and animate--* classes)
 * @param {Document|Element} rootEl - Root element to search within
 * @returns {Array<Element>} Array of animation trigger elements
 */
function getAnimationTriggerElements(rootEl = document) {
  const elements = new Set();
  
  // Get scroll-trigger elements
  const scrollTriggerElements = rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME);
  Array.from(scrollTriggerElements).forEach(el => elements.add(el));
  
  // Get animate--* elements
  SUPPORTED_ANIMATION_TYPES.forEach(type => {
    const className = `${ANIMATE_PREFIX}${type}`;
    const animateElements = rootEl.getElementsByClassName(className);
    Array.from(animateElements).forEach(el => {
      // Only add if not already a scroll-trigger (avoid duplicates)
      if (!el.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME)) {
        elements.add(el);
      }
    });
  });
  
  return Array.from(elements);
}

/**
 * IntersectionObserver callback for scroll animations
 * @param {Array<IntersectionObserverEntry>} entries - Observer entries
 * @param {IntersectionObserver} observer - Observer instance
 */
function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    const element = entry.target;
    
    if (entry.isIntersecting) {
      // Element entered viewport - animate in
      if (element.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        // Apply cascade delay if specified
        if (element.hasAttribute("data-cascade")) {
          const delay = element.dataset.cascadeDelay || index;
          element.style.setProperty('--animation-order', delay);
        }
        
        // Apply custom delay if specified
        if (element.hasAttribute("data-animation-delay")) {
          const delay = parseFloat(element.dataset.animationDelay) || 0;
          element.style.setProperty('--animation-delay', `${delay}ms`);
        }
        
        // Remove offscreen class to trigger animation
        element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      }
      
      // Unobserve if animation should only play once
      const animateOnce = element.dataset.animateOnce !== 'false'; // default to true
      if (animateOnce) {
        observer.unobserve(element);
      }
    } else {
      // Element left viewport - reset if repeat is enabled
      const animateOnce = element.dataset.animateOnce !== 'false';
      if (!animateOnce) {
        element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      }
    }
  });
}

/**
 * Initialize scroll animation triggers
 * @param {Document|Element} rootEl - Root element to initialize within
 * @param {boolean} isDesignModeEvent - Whether this is a theme editor event
 */
function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = getAnimationTriggerElements(rootEl);
  
  if (animationTriggerElements.length === 0) return;

  // Design mode handling - show elements immediately unless animation_design_mode is enabled
  if (isDesignModeEvent) {
    const animationDesignMode = document.body.dataset.animationDesignMode === 'true';
    if (!animationDesignMode) {
      animationTriggerElements.forEach((element) => {
        element.classList.add(SCROLL_ANIMATION_DESIGN_MODE_CLASSNAME);
        element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      });
      return;
    }
  }

  // Skip if reduced motion is preferred
  if (prefersReducedMotion()) {
    animationTriggerElements.forEach((element) => {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    });
    return;
  }

  // Get animation trigger margin from CSS variable (set by theme settings)
  const rootMargin = getComputedStyle(document.documentElement)
    .getPropertyValue('--animation-trigger-margin')
    .trim() || "0px 0px -50px 0px";

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: rootMargin,
    threshold: 0.01, // Trigger when 1% of element is visible
  });
  
  // Initialize all elements: add offscreen class first, then observe
  // This ensures elements start hidden and animate in when they enter viewport
  animationTriggerElements.forEach((element) => {
    // Add offscreen class immediately so element starts hidden
    element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    // Then observe - if already in viewport, observer will immediately remove offscreen class
    observer.observe(element);
  });
}

/**
 * Calculate percentage of element visible in viewport
 * @param {Element} element - Element to calculate percentage for
 * @returns {number} Percentage (0-100)
 */
function percentageSeen(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const elementHeight = rect.height;
  
  // Element completely above viewport
  if (rect.bottom < 0) return 0;
  
  // Element completely below viewport
  if (rect.top > viewportHeight) return 0;
  
  // Element is in viewport - calculate visible percentage
  const visibleTop = Math.max(0, rect.top);
  const visibleBottom = Math.min(viewportHeight, rect.bottom);
  const visibleHeight = visibleBottom - visibleTop;
  
  return Math.round((visibleHeight / viewportHeight) * 100);
}

/**
 * Update zoom animation using requestAnimationFrame for smoother performance
 * @param {Element} element - Element to animate
 * @param {number} scaleAmount - Scale amount multiplier
 */
function updateZoomAnimation(element, scaleAmount) {
  const percentage = percentageSeen(element);
  const zoomRatio = 1 + scaleAmount * (percentage / 100);
  
  // Use requestAnimationFrame for smoother animations
  requestAnimationFrame(() => {
    element.style.setProperty("--zoom-in-ratio", zoomRatio);
  });
}

/**
 * Initialize scroll zoom animation (parallax-style zoom effect)
 */
function initializeScrollZoomAnimationTrigger() {
  // Respect user's reduced motion preference if theme setting is enabled
  if (prefersReducedMotion()) return;

  const animationTriggerElements = Array.from(
    document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)
  );

  if (animationTriggerElements.length === 0) return;

  // Get zoom scale from CSS variable (set by theme settings)
  const zoomScale = parseFloat(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--animation-zoom-scale')
      .trim()
  ) || 0.5;
  
  const scaleAmount = zoomScale / 100;

  // Create a single throttled handler for all zoom elements
  const handleZoomScroll = throttle(() => {
    animationTriggerElements.forEach((element) => {
      const state = zoomAnimationState.get(element);
      if (state && state.isVisible) {
        updateZoomAnimation(element, scaleAmount);
      }
    });
  }, 16); // ~60fps

  animationTriggerElements.forEach((element) => {
    const state = {
      isVisible: false,
      observer: null,
      scrollHandler: null,
    };
    
    // Track visibility with IntersectionObserver
    state.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        state.isVisible = entry.isIntersecting;
        // Update immediately when visibility changes
        if (state.isVisible) {
          updateZoomAnimation(element, scaleAmount);
        }
      });
    }, {
      rootMargin: '50% 0px', // Extend trigger zone
    });
    
    state.observer.observe(element);
    
    // Store state for cleanup
    zoomAnimationState.set(element, state);
    
    // Set initial zoom ratio
    updateZoomAnimation(element, scaleAmount);
  });
  
  // Remove previous scroll listener if re-initializing (e.g. theme editor section reload)
  if (zoomAnimationState._globalScrollHandler) {
    window.removeEventListener("scroll", zoomAnimationState._globalScrollHandler);
  }

  // Add scroll listener once for all elements
  window.addEventListener("scroll", handleZoomScroll, { passive: true });
  zoomAnimationState._globalScrollHandler = handleZoomScroll;
}

/**
 * Initialize all animations
 */
function initializeAnimations() {
  // Check if animations are enabled via theme settings
  const animationsEnabled = document.body.dataset.animationsEnabled !== 'false';
  const zoomEnabled = document.body.dataset.zoomAnimationEnabled !== 'false';
  
  if (animationsEnabled) {
    initializeScrollAnimationTrigger();
  }
  
  if (zoomEnabled) {
    initializeScrollZoomAnimationTrigger();
  }
}

// Initialize animations on page load
if (document.readyState === 'loading') {
  window.addEventListener("DOMContentLoaded", initializeAnimations);
} else {
  // DOM already loaded
  initializeAnimations();
}

// Theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    initializeScrollAnimationTrigger(event.target, true);
  });
  
  document.addEventListener("shopify:section:reorder", () => {
    initializeScrollAnimationTrigger(document, true);
  });
  
  document.addEventListener("shopify:block:select", () => {
    // Reinitialize to show animations in design mode if enabled
    initializeScrollAnimationTrigger(document, true);
  });
}
